import { HttpClient, HttpEventType, HttpRequest, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IAttachment } from '@valhalla/data/entities';
import { BehaviorSubject, fromEvent, merge, Observable } from 'rxjs';
import { skip, takeUntil, tap, map, debounceTime } from 'rxjs/operators';

import { DataHttpCommonService } from '../data-http-common.service';
import { EndpointUrlConfig } from '../endpoint.config';
import type { IEndpointUrlConfig } from '../endpoint.config';
import {
	IAttachUploadParams,
	IAttachUploadResult,
	IUploadResult,
	FileDataHttpService,
	IFileDialogResult,
	IFileDialogOptions,
	IPreuploadedFilesResponse,
} from './abstract';
import { guid, delayedRetry } from '@valhalla/utils';

@Injectable()
export class FileDataHttpServiceImpl implements FileDataHttpService {
	constructor(
		@Inject(EndpointUrlConfig) public readonly config: IEndpointUrlConfig,
		public readonly http: HttpClient,
		public readonly common: DataHttpCommonService
	) {}

	mapFileToAttachment(file: File): Partial<IAttachment> {
		return {
			file: file,
			fileName: file.name,
			filesize: file.size,
			guid: guid(),
			mime: <any>file.type,
			name: file.name,
		};
	}

	preUploadFile(params: IAttachUploadParams): IAttachUploadResult {
		if (!params || !params.attach) {
			return null;
		}
		const url = this.common.getEndpointUrl(this.config.file.preUploadFile);
		return this.uploadFileToUrl(params, url);
	}

	ToPreUploadedFiles(formData: FormData): Observable<IPreuploadedFilesResponse> {
		const url = '/api/files/upload/ToPreUploadedFiles';
		return this.http.post<IPreuploadedFilesResponse>(url, formData);
	}

	uploadFile(params: IAttachUploadParams): IAttachUploadResult {
		if (!params || !params.attach) {
			return null;
		}
		const url = this.common.getEndpointUrl(this.config.file.uploadFile);
		return this.uploadFileToUrl(params, url);
	}

	openFileDialog(options?: Partial<IFileDialogOptions>) {
		options = Object.assign(this.defaultFileDialogOptions(), options);

		return new Observable<IFileDialogResult>(observer => {
			const inputEl = document.createElement('input');
			inputEl.type = 'file';
			inputEl.style.display = 'none';
			inputEl.multiple = options.multiple;

			function onPickFiles(e: Event) {
				// if (e instanceof FocusEvent) {
				// 	return observer.complete();
				// }
				const files = Array.from((<HTMLInputElement>e.target).files);
				inputEl.value = null;
				observer.next({ files });
				observer.complete();
			}

			// const focus$ = fromEvent<FocusEvent>(document.body, 'focus', { capture: true }).pipe(debounceTime(100));
			const change$ = fromEvent(inputEl, 'change');
			const pickFiles$ = merge(/*focus$, */ change$).subscribe(onPickFiles);

			document.body.appendChild(inputEl);
			inputEl.click();

			function dispose() {
				pickFiles$.unsubscribe();
				document.body.removeChild(inputEl);
			}

			return () => {
				dispose();
			};
		});
	}

	protected defaultFileDialogOptions(): Partial<IFileDialogOptions> {
		return {
			multiple: true,
		};
	}

	protected uploadFileToUrl(params: IAttachUploadParams, url: string): IAttachUploadResult {
		const taskId = Number(params.taskId);
		if (taskId > 0) {
			url += `?taskId=${taskId}`;
		}

		const formData = new FormData();
		formData.append(`file-0`, params.attach.file, params.attach.name);

		const httpHeaders = new HttpHeaders().append('ngsw-bypass', 'true');

		const req = new HttpRequest('POST', url, formData, {
			reportProgress: true,
			headers: httpHeaders,
			responseType: 'text',
		});
		const upload = this.buildProgressiveRequest<string>(req, params.cancel$);

		return {
			progressPercent$: upload.progressPercent$,
			result$: upload.result$.pipe(
				map(attachId => {
					params.attach.id = parseInt(attachId, 10);
					return params.attach;
				})
			),
			cancel: () => upload.cancel(),
		};
	}

	protected buildProgressiveRequest<R = any, T = any>(
		req: HttpRequest<T>,
		cancel$?: Observable<any>
	): IUploadResult<R> {
		const result$ = new BehaviorSubject<R>(null);
		const progress$ = new BehaviorSubject<number>(1);
		let returnResult$ = result$.pipe(skip(1));
		let returnProgressPercent$ = progress$.asObservable();
		let req$ = this.http.request<R>(req).pipe(
			delayedRetry(3000),
			tap(event => {
				switch (event.type) {
					case HttpEventType.UploadProgress:
						const percentDone = Math.round((100 * event.loaded) / event.total);
						return progress$.next(percentDone);
					case HttpEventType.Response:
						return result$.next(event.body);
				}
			})
		);
		if (cancel$) {
			returnResult$ = result$.pipe(takeUntil(cancel$));
			returnProgressPercent$ = progress$.pipe(takeUntil(cancel$));
			req$ = req$.pipe(takeUntil(cancel$));
		}
		const reqSubscription = req$.subscribe({
			error: err => {
				result$.error(err.message || err.error);
			},
			complete: () => {
				result$.complete();
				progress$.complete();
			},
		});
		return {
			result$: returnResult$,
			progressPercent$: returnProgressPercent$,
			cancel: () => reqSubscription.unsubscribe(),
		};
	}
}
